import { useEffect, useState, useRef } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Icon, IconButton, Input } from "@chakra-ui/react";
import { MessageCircle, Camera, Flag, X, ImagePlus, Wrench, Hammer, ClipboardCheck } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { colors } from "@/theme/colors";

const ALLOWED = /image\/(jpeg|jpg|png|webp|heic|heif)/i;
const EXT_OK = /\.(jpe?g|png|webp|heic|heif)$/i;
const MAX_MB = 10;
const GREY_BG = "#ECEBE8";
const ICON_GREY = "#5B5B57";

export const FLAG_ACTIONS = [
  { type: "Corrective Action", icon: ClipboardCheck, desc: "Document a deficiency and how it will be fixed" },
  { type: "Equipment/Tool Request", icon: Hammer, desc: "Request equipment or tools for the job" },
  { type: "Maintenance Request", icon: Wrench, desc: "Report equipment that needs maintenance" },
];

const Sheet = ({ title, onClose, children, testId }) => {
  const panelRef = useRef(null);
  // Focus once on mount only — re-running focus() on re-render steals focus
  // from inputs inside the sheet while the user is typing.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <Box position="fixed" inset="0" zIndex={2147483200} data-testid={testId}>
      <Box position="absolute" inset="0" bg="rgba(31,41,55,0.5)" onClick={onClose} />
      <Box ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} outline="none"
        position="absolute" left="0" right="0" bottom="0" bg={colors.white}
        borderTopRadius="18px" p="5" pb="8" maxH="85vh" overflowY="auto" mx="auto" maxW="560px">
        <Flex justify="space-between" align="center" mb="4">
          <Text fontWeight="700" fontSize="md" color={colors.textPrimary}>{title}</Text>
          <IconButton aria-label="Close" size="sm" variant="ghost" onClick={onClose} data-testid="qa-close"><X size={17} /></IconButton>
        </Flex>
        {children}
      </Box>
    </Box>
  );
};

const SheetRow = ({ icon, label, desc, onClick, testId, tint = colors.tealTint, fg = colors.teal }) => (
  <Flex as="button" type="button" onClick={onClick} data-testid={testId} w="full" textAlign="left"
    align="center" gap="3" px="4" py="3.5" borderRadius="12px" border="1px solid" borderColor={colors.border}
    _hover={{ borderColor: fg, bg: tint }} transition="border-color 0.15s, background-color 0.15s">
    <Flex align="center" justify="center" w="38px" h="38px" borderRadius="10px" bg={tint} flexShrink={0}>
      <Icon as={icon} boxSize="5" color={fg} />
    </Flex>
    <Box>
      <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{label}</Text>
      {desc && <Text fontSize="xs" color={colors.textSecondary}>{desc}</Text>}
    </Box>
  </Flex>
);

const PillIcon = ({ icon, active, onClick, label, testId }) => (
  <Flex as="button" type="button" onClick={onClick} aria-label={label} data-testid={testId}
    align="center" justify="center" w="34px" h="30px" borderRadius="full"
    color={active ? colors.teal : ICON_GREY}
    _hover={{ color: colors.teal }} _active={{ transform: "scale(0.92)" }} transition="color 0.15s, transform 0.1s">
    <Icon as={icon} boxSize="4.5" strokeWidth={2.1} />
  </Flex>
);

// SiteDocs-style capsule: chat / camera / flag icons always visible, no labels.
// `scope` = null (whole report) | { sectionId, sectionTitle } | + { fieldId, fieldLabel }.
export function QuickPill({ scope, counts = {}, commentOpen, onToggleComment, onAddPhotos, onRaise }) {
  const [sheet, setSheet] = useState(null); // 'camera' | 'flag'
  const [showCam, setShowCam] = useState(false);
  const [err, setErr] = useState(null);
  const galleryRef = useRef(null);
  const scopeLabel = scope?.fieldLabel || scope?.sectionTitle || "Whole report";
  const tid = scope?.fieldId ? `field-${scope.fieldId}` : scope?.sectionId ? `section-${scope.sectionId}` : "report";

  const close = () => { setSheet(null); setErr(null); };

  const pickGallery = async (e) => {
    setErr(null);
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const good = [];
    for (const file of files) {
      const typeOk = file.type ? ALLOWED.test(file.type) : EXT_OK.test(file.name || "");
      if (!typeOk) { setErr(`"${file.name}" isn't a supported image (JPG, PNG, WEBP or HEIC).`); continue; }
      if (file.size > MAX_MB * 1024 * 1024) { setErr(`"${file.name}" is over ${MAX_MB} MB.`); continue; }
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      good.push({ name: file.name || "photo.jpg", type: file.type || "image/jpeg", dataUrl });
    }
    if (good.length) { onAddPhotos(good, scope); close(); }
  };

  const takenShot = (dataUrl) => {
    setShowCam(false);
    onAddPhotos([{ name: `camera-${Date.now()}.jpg`, type: "image/jpeg", dataUrl }], scope);
    close();
  };

  return (
    <>
      <Flex bg={GREY_BG} borderRadius="full" px="1" py="0.5" gap="0" align="center"
        flexShrink={0} data-testid={`qa-pill-${tid}`}>
        <PillIcon icon={MessageCircle} label="Comment" active={commentOpen || counts.comments > 0}
          onClick={() => onToggleComment(scope)} testId={`qa-comment-${tid}`} />
        <PillIcon icon={Camera} label="Camera" active={counts.photos > 0}
          onClick={() => setSheet("camera")} testId={`qa-camera-${tid}`} />
        <PillIcon icon={Flag} label="Flag" active={sheet === "flag"}
          onClick={() => setSheet("flag")} testId={`qa-flag-${tid}`} />
      </Flex>

      {sheet === "camera" && (
        <Sheet title="Attach photos" onClose={close} testId="qa-camera-modal">
          <Text fontSize="xs" color={colors.textSecondary} mb="3">Attach to: <b>{scopeLabel}</b> · JPG, PNG, WEBP or HEIC · max {MAX_MB} MB each</Text>
          <Stack gap="2.5">
            <SheetRow icon={Camera} label="Take a picture" desc="Open the camera"
              onClick={() => setShowCam(true)} testId="qa-take-picture" />
            <SheetRow icon={ImagePlus} label="Photo Gallery" desc="Choose from your photos"
              onClick={() => galleryRef.current?.click()} testId="qa-photo-gallery" />
          </Stack>
          <input type="file" accept="image/*" multiple ref={galleryRef} onChange={pickGallery}
            data-testid="qa-gallery-input" style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} />
          {err && <Text mt="3" fontSize="sm" color={colors.criticalText} data-testid="qa-photo-error">{err}</Text>}
        </Sheet>
      )}

      {sheet === "flag" && (
        <Sheet title="Raise an action" onClose={close} testId="qa-flag-modal">
          <Text fontSize="xs" color={colors.textSecondary} mb="3">
            Linked to: <b>{scopeLabel}</b> — the new form opens now and stays traceable back to this report.
          </Text>
          <Stack gap="2.5">
            {FLAG_ACTIONS.map((a) => (
              <SheetRow key={a.type} icon={a.icon} label={a.type} desc={a.desc}
                onClick={() => { onRaise(a.type, scope); close(); }}
                testId={`qa-flag-${a.type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            ))}
          </Stack>
        </Sheet>
      )}

      {showCam && <CameraCapture onCapture={takenShot} onClose={() => setShowCam(false)} />}
    </>
  );
}

// Inline note editor shown directly below the field (SiteDocs style).
export function InlineCommentBox({ scopeKey, onSave, onClose }) {
  const [text, setText] = useState("");
  return (
    <Box mt="2" bg={GREY_BG} borderRadius="12px" px="3.5" pt="2.5" pb="3" data-testid={`qa-comment-box-${scopeKey}`}>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Your comment" autoFocus
        variant="flushed" fontSize="sm" color={colors.textPrimary} borderColor="#C9C7C1"
        _focus={{ borderColor: colors.teal }} data-testid={`qa-comment-input-${scopeKey}`}
        onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) { onSave(text.trim()); } }} />
      <HStack justify="flex-end" mt="2" gap="1">
        <Button size="xs" variant="ghost" onClick={onClose} data-testid={`qa-comment-cancel-${scopeKey}`}>Cancel</Button>
        <Button size="xs"
          disabled={!text.trim()} onClick={() => onSave(text.trim())} data-testid={`qa-comment-save-${scopeKey}`}>Save</Button>
      </HStack>
    </Box>
  );
}

// Saved notes rendered under the field they belong to.
export function SavedNotes({ notes }) {
  if (!notes.length) return null;
  return (
    <Stack gap="1.5" mt="2">
      {notes.map((c) => (
        <Flex key={c.id} align="flex-start" gap="2" bg={GREY_BG} borderRadius="10px" px="3" py="2" data-testid={`qa-note-${c.id}`}>
          <Icon as={MessageCircle} boxSize="3.5" color={colors.teal} mt="0.5" flexShrink={0} />
          <Text fontSize="sm" color={colors.textPrimary}>{c.body}</Text>
        </Flex>
      ))}
    </Stack>
  );
}
